/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect, useState } from 'react';
import useTixlySession from 'effects/useTixlySession';
import useGADataLayer from 'effects/gaDataLayer';
import Modal from 'components/Modal/Modal';
import Login, { type LoginProps } from 'components/Menu/Login/Login';
import './Authorize.scss';

const DEFAULT_LOGIN: NonNullable<AuthorizeProps['login']> = {};
const DEFAULT_PURCHASE: NonNullable<AuthorizeProps['purchase']> = {};

const DEFAULT_LABELS = {
  purchase: 'Purchase',
  login: 'Login',
};

interface AuthorizeProps {
  itixUrl?: string;
  login?: {
    type?: string;
    loginLink?: string;
    heading?: string;
    title?: string;
    body?: string;
    footer?: string;
  };
  purchase?: {
    heading?: string;
    title?: string;
    body?: string;
    footer?: string;
  };
  sku: string;
  name: string;
  price?: number | string;
  basket?: string;
  labels?: { purchase: string; login: string };
}

/**
 * This is the Authorize component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Authorize({
  itixUrl = '',
  sku,
  name,
  login: loginConfig = DEFAULT_LOGIN,
  purchase: purchaseConfig = DEFAULT_PURCHASE,
  price = 0,
  basket = '',
  labels = DEFAULT_LABELS,
}: AuthorizeProps) {
  const {
    client: { saved },
  } = useTixlySession(itixUrl);
  const { trackItemView, trackSelectItem } = useGADataLayer();
  const [login, setLogin] = useState(false);

  useEffect(() => {
    trackItemView({
      item_id: sku,
      item_name: name,
      item_category: 'digitalt program',
      item_list_id: 'digitale-program',
      item_list_name: 'Digitale program',
      price: price as number,
      quantity: 1,
    });
  }, []);

  async function onBasketClick() {
    await trackSelectItem({
      item_id: sku,
      item_name: name,
      item_category: 'digitalt program',
      item_list_id: 'digitale-program',
      item_list_name: 'Digitale program',
      price: price as number,
      quantity: 1,
    });

    location.href = basket;
  }

  function renderPrice() {
    return (
      <div className="price">
        {price}
        <small>kr</small>
      </div>
    );
  }

  function renderBasket() {
    return (
      <button className="ticket-button" onClick={onBasketClick}>
        <span>{labels.purchase}</span>
      </button>
    );
  }

  function renderLoginAction() {
    if (loginConfig.type === 'link') {
      return (
        <a className="login-link" href={loginConfig.loginLink}>
          {labels.login}
        </a>
      );
    }

    return (
      <button
        onClick={() => {
          setLogin(true);
        }}
      >
        {labels.login}
      </button>
    );
  }

  function renderContent() {
    if (saved && loginConfig.type !== 'link') {
      return (
        <>
          <h2 className="category">{purchaseConfig.heading}</h2>
          <h3 className="h2">{purchaseConfig.title}</h3>
          <p>{purchaseConfig.body}</p>
          {renderPrice()}
          {renderBasket()}
          <footer>{purchaseConfig.footer}</footer>
        </>
      );
    }

    return (
      <>
        <h2 className="category">{loginConfig.heading}</h2>
        <h3 className="h2">{loginConfig.title}</h3>
        <p>{loginConfig.body}</p>
        {renderPrice()}
        {renderBasket()}
        <footer>
          {loginConfig.footer}
          {renderLoginAction()}
        </footer>
      </>
    );
  }

  if (login) {
    return (
      <Login
        {...(loginConfig as unknown as LoginProps)}
        close
        onClose={() => {
          setLogin(false);
        }}
        onSuccess={() => {
          setLogin(false);
        }}
      />
    );
  }

  return <Modal className="authorize">{renderContent()}</Modal>;
}

export default Authorize;
